import { useState, useEffect } from 'react';
import { detectDeviceBySize, getDetailedScreenInfo, ScreenSizeInfo } from '../utils/screenSizeDetection.utils';

/**
 * Hook para detectar dispositivos basándose en dimensiones de pantalla
 */
export function useScreenSizeDetection(): ScreenSizeInfo {
  const [screenInfo, setScreenInfo] = useState<ScreenSizeInfo>(() => detectDeviceBySize());

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScreenInfo(detectDeviceBySize());
      }, 200);
    };

    // Escuchar cambios de tamaño
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return screenInfo;
}

/**
 * Hook para obtener información detallada de la pantalla
 */
export function useDetailedScreenInfo() {
  const [screenInfo, setScreenInfo] = useState(() => getDetailedScreenInfo());

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScreenInfo(getDetailedScreenInfo());
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return screenInfo;
}

/**
 * Hook simplificado para detectar si es móvil por dimensiones
 */
export function useIsMobileBySize(): boolean {
  const screenInfo = useScreenSizeDetection();
  return screenInfo.isMobile;
}

/**
 * Hook simplificado para detectar si es tablet por dimensiones
 */
export function useIsTabletBySize(): boolean {
  const screenInfo = useScreenSizeDetection();
  return screenInfo.isTablet;
}

/**
 * Hook simplificado para detectar si es escritorio por dimensiones
 */
export function useIsDesktopBySize(): boolean {
  const screenInfo = useScreenSizeDetection();
  return screenInfo.isDesktop;
}

/**
 * Hook para detectar orientación basándose en dimensiones
 */
export function useOrientationBySize(): 'portrait' | 'landscape' {
  const screenInfo = useScreenSizeDetection();
  return screenInfo.orientation;
}

/**
 * Hook para detectar si es un móvil pequeño
 */
export function useIsSmallMobileBySize(): boolean {
  const screenInfo = useScreenSizeDetection();
  return screenInfo.isSmallMobile;
}

/**
 * Hook para detectar si es un móvil grande
 */
export function useIsLargeMobileBySize(): boolean {
  const screenInfo = useScreenSizeDetection();
  return screenInfo.isLargeMobile;
}
